import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import Mandala from "@/components/Mandala";
import SEO from "@/components/SEO";

// Fallbacks used when Holly hasn't set an image for a slot in Admin → Page
// Images yet. These reference known-working Emergent-hosted artifacts so the
// page never renders broken tiles. Original hardcoded WP URLs were killed
// during the avianayoga.com DNS cutover.
const FALLBACKS = {
  hero:              "/api/files/2954dd4f-d431-4797-a7c8-838938f7b6cb",
  "horse-portrait":  "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/uvz6fcvb_IMG_8274.jpeg",
  "healer-portrait": "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/3wv6bhuj_PXL_20240904_161550046.PORTRAIT~4.jpg",
  "retreat-portrait":"https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/nk1vssa4_SEDONARETREAT-52.jpg",
};

const EDUCATION = {
  "Formal": [
    "2004 — Associate's Degree in Science · McHenry County College, Crystal Lake IL",
    "2007 — Bachelor's Degree in Equine Science · Colorado State University, Fort Collins CO",
  ],
  "Yoga & Movement": [
    "AFPA Personal Trainer Certification",
    "200-Hour Yoga Certification · Breathe Yoga, Fort Collins CO",
    "Aerial Yoga Teacher Training · Aircats, Boulder CO",
    "TRX Suspension Training Certification · Denver CO",
    "ZUMBA Instructor Certification Level 1 & 2 · Denver CO",
  ],
  "Reiki & The Healing Arts": [
    "Reiki II, Usui Shiki Ryoho · Fort Collins CO",
    "Reiki Master, Usui Shiki Ryoho · Fort Collins CO",
    "Aromatherapy Certification · Institute of Spiritual Healing & Aromatherapy, Naperville IL",
    "Certified Nutritional Therapy Practitioner · The Nutritional Therapy Institute, Denver CO",
    "Apprenticeship in Shamanic & Ceremonial Practices",
    "Thai Yoga Bodywork Training · Soderworld, Willowbrook IL",
    "Young Living School of Natural Remedies · Denver CO",
    "Blood Chemistry Analysis · APEX Energetics, Del Mar CA",
  ],
  "Equine Studies": [
    "Equine Massage Therapy Certification · Indian Creek Equine Rehab, Worden IL",
  ],
};

export default function About() {
  const [images, setImages] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/page-images/about");
        setImages(data?.slots || {});
      } catch {}
    })();
  }, []);
  const img = (key) => images[key]?.[0] || FALLBACKS[key];

  return (
    <div data-testid="about-holly-page">
      <SEO
        title="About Holly Johnson · Yoga Alliance E-RYT 500 · Reiki Master · Founder of Aviana Yoga"
        description="Holly Johnson is a Yoga Alliance E-RYT 500 aerial yoga teacher trainer, Usui-Holy Fire III Reiki Master, and women's retreat leader. Based in Sugar Grove, IL — traveling worldwide leading sacred sister gatherings."
        path="/about"
        keywords="Holly Johnson yoga, Aviana Yoga founder, E-RYT 500, Reiki Master, women's retreat leader, aerial yoga teacher trainer"
      />
      {/* HERO */}
      <section className="relative min-h-[85svh] flex items-center overflow-hidden bg-aviana-plumDeep text-aviana-cream">
        <img src={img("hero")} alt="" className="absolute inset-0 w-full h-full object-cover object-[40%_center]" />
        <div className="absolute inset-0 bg-gradient-to-r from-aviana-plumDeep via-aviana-plumDeep/80 to-aviana-plumDeep/10" />
        <div className="absolute inset-0 grain pointer-events-none opacity-50" aria-hidden />
        {/* Hero spinning mandala — anchored lower-left so the headline copy remains highly readable */}
        <Mandala size={620} variant="full" className="absolute -left-44 top-[60%] text-aviana-gold opacity-[0.10] animate-spin-slow pointer-events-none" />
        <div className="container-aviana relative z-10 py-32">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-7">
              <Mandala size={28} variant="lotus" className="text-aviana-goldSoft" />
              <p className="overline text-aviana-goldSoft">Meet Holly Johnson</p>
            </div>
            <h1 className="font-display text-[64px] sm:text-[88px] lg:text-[110px] leading-[0.92]" data-testid="about-headline">
              Teacher, healer,
              <br />
              <em className="italic font-serif gold-text">guide.</em>
            </h1>
            <p className="mt-10 font-serif italic text-[20px] md:text-[24px] leading-[1.45] text-aviana-cream/90 max-w-xl">
              <strong className="not-italic font-medium text-aviana-cream">Since 2014,</strong> Aviana has been the unfolding of a vision rooted in <em>divine feminine energy</em> — a path of embodiment, transformation, and service.
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-5 text-[11px] tracking-[0.28em] uppercase text-aviana-goldSoft">
              <span>♍ Virgo Sun</span>
              <span className="w-6 h-px bg-aviana-goldSoft/40" />
              <span>♏ Scorpio Moon</span>
              <span className="w-6 h-px bg-aviana-goldSoft/40" />
              <span>♒ Aquarius Rising</span>
            </div>
          </div>
        </div>
      </section>

      {/* OPENING — Hello & Welcome */}
      <section className="py-28 md:py-36 relative overflow-hidden">
        <Mandala size={520} variant="lotus" className="absolute -right-32 -top-20 text-aviana-gold opacity-[0.06] animate-spin-slow pointer-events-none hidden md:block" />
        <Mandala size={320} variant="rose" className="absolute -left-24 bottom-12 text-aviana-plum opacity-[0.07] pointer-events-none hidden md:block" />
        <div className="container-aviana max-w-3xl relative">
          <p className="overline-plum mb-5">Hello &amp; welcome</p>
          <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-[1.05]">
            I'm Holly — a <em className="italic font-serif gold-text">teacher, healer, and guide.</em>
          </h2>
          <p className="mt-10 font-serif italic text-[22px] md:text-[24px] leading-[1.5] text-aviana-inkSoft">
            My work is devoted to helping others <em>reconnect with their inner wisdom</em> through movement, energy, and presence.
          </p>
          <p className="mt-8 text-aviana-muted leading-[1.95] text-[17px]">
            Since 2014, Aviana has been the unfolding of a vision rooted in divine feminine energy — a path of embodiment, transformation, and service. I weave together fourteen-plus years of yoga, equine wisdom, ceremonial practice, and the healing arts into spaces where women remember the wilder, truer version of themselves.
          </p>
          <p className="mt-7 font-serif italic text-[18px] text-aviana-plum">
            Many blessings of love, light &amp; flight,
            <br />
            <span className="text-aviana-ink font-display not-italic text-2xl">Holly Johnson</span>
          </p>
        </div>
      </section>

      {/* TEACHER & FACILITATOR */}
      <section className="py-28 md:py-36 bg-aviana-surface relative overflow-hidden">
        <Mandala size={680} variant="full" className="absolute -left-40 top-1/3 text-aviana-gold opacity-[0.05] animate-spin-slow pointer-events-none hidden md:block" />
        <div className="container-aviana grid lg:grid-cols-12 gap-14 items-center relative">
          <div className="lg:col-span-5">
            <div className="relative max-w-md mx-auto">
              <div className="absolute -inset-3 rounded-[2.5rem] bg-gradient-to-br from-aviana-goldSoft to-aviana-plum opacity-20 blur-2xl" />
              <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden ring-2 ring-aviana-gold/40">
                <img src={img("horse-portrait")} alt="Holly with the horses" className="w-full h-full object-cover" />
              </div>
              <Mandala size={64} variant="star" className="absolute -top-4 -right-4 text-aviana-gold opacity-80" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="overline-plum mb-5">As a teacher &amp; facilitator</p>
            <h2 className="font-display text-4xl md:text-6xl text-aviana-ink leading-[1.05]">
              Horses were my <em className="italic font-serif gold-text">first portal.</em>
            </h2>
            <div className="mt-10 space-y-6 text-aviana-muted leading-[1.95] text-[17px]">
              <p>
                In my late teens I enrolled in equine massage therapy training. There I met <strong className="text-aviana-ink font-medium">Jack</strong> — a rescued thoroughbred with a tragic story involving stifle and conformation issues. Having just undergone double knee surgery myself, our journeys mirrored one another. I adopted Jack, and together we walked the path of healing and mutual transformation.
              </p>
              <p>
                Through Jack, I began teaching. What started as guiding an eleven-year-old girl in the arena became the foundation of my facilitation style: <em>tuning into subtle cues, holding a calm presence, and creating space for authentic connection.</em>
              </p>
              <p>
                In my early twenties at Colorado State University studying Equine Science, I mentored in an equine therapy program for youth. Witnessing a young woman transform through her relationship with a horse confirmed my calling to serve as a guide for others.
              </p>
              <p>
                My motto since: <em className="text-aviana-ink not-italic font-medium">set the student up for success.</em> I read the energy of a room, adapt to different learning styles, and provide tools for integration — so each person feels supported in their own awakening.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HEALER */}
      <section className="py-28 md:py-36">
        <div className="container-aviana grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <p className="overline mb-5">As a healer</p>
            <h2 className="font-display text-4xl md:text-6xl text-aviana-ink leading-[1.05]">
              A clairvoyant &amp; <em className="italic font-serif gold-text">clairsentient path.</em>
            </h2>
            <div className="mt-10 space-y-6 text-aviana-muted leading-[1.95] text-[17px]">
              <p>
                My healing path began early, through moments of deep awakening that reshaped the course of my life. At nineteen — after pursuing equine massage training — I found my way to <strong className="text-aviana-ink font-medium">Reiki energy healing</strong>. Once attuned, I began applying the practice with the horses I worked on, and developed a gift of receiving <em>intuitive impressions</em> through sensations, images, and energetic feedback that revealed layers of their story and what was needed for balance and release.
              </p>
              <p>
                From there I immersed in aromatherapy, Reiki master training, shamanic practices, and animal communication. I apprenticed with mentors in sacred ceremony and ritual — drum journeying, sweat lodge (Inipi), and integration circles. Each step deepened my ability to listen, attune, and hold a healing space.
              </p>
              <p>
                When I became an aerial yoga instructor, I bridged these gifts into human healing. In restorative aerial workshops I offered <em>Reiki "recharges"</em> while students rested in supported postures. These moments showed me how seamlessly movement and energy medicine intertwine.
              </p>
              <p>
                Today I serve as a <strong className="text-aviana-ink font-medium">clairvoyant and clairsentient healer</strong>, weaving intuitive guidance, energy alignment, and sacred presence. My intention is not only to support healing — but to support each woman in reclaiming her sovereignty, wisdom, and light.
              </p>
            </div>
            <Link to="/healing" className="btn-ghost-gold mt-10 inline-flex">Explore healing sessions <ArrowRight size={16} /></Link>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="relative max-w-md mx-auto">
              <div className="absolute -inset-3 rounded-[2.5rem] bg-gradient-to-br from-aviana-goldSoft to-aviana-plum opacity-20 blur-2xl" />
              <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden ring-2 ring-aviana-gold/40">
                <img src={img("healer-portrait")} alt="Holly teaching" className="w-full h-full object-cover" />
              </div>
              <Mandala size={64} variant="rose" className="absolute -bottom-4 -left-4 text-aviana-plum opacity-80" />
            </div>
          </div>
        </div>
      </section>

      {/* BLESSINGS OF AVIANA — the goddess's birth story */}
      <section className="py-28 md:py-36 bg-aviana-plumDeep text-aviana-cream relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none opacity-60" aria-hidden />
        <Mandala size={700} variant="full" className="absolute -right-20 -bottom-20 text-aviana-gold opacity-[0.08] animate-spin-slow" />
        <div className="container-aviana relative max-w-4xl">
          <p className="overline mb-5 text-aviana-goldSoft">The blessings of Aviana</p>
          <h2 className="font-display text-5xl md:text-7xl leading-[1.0]">
            Aviana answered <em className="italic font-serif gold-text">the call.</em>
          </h2>
          <div className="mt-12 space-y-6 text-aviana-cream/85 leading-[1.95] text-[17px]">
            <p className="font-serif italic text-[20px] md:text-[22px] leading-[1.5] text-aviana-cream/95">
              Since Aviana's inception from the Spirit realm — to serve as the archetype of embodied <em>divine feminine</em> energy as we traverse this Golden Age of Enlightenment with open hearts and wings — her blessings have been abundant.
            </p>
            <p>
              When I look back over the Aviana journey, I can see where she ignited the calling that put everything in motion. Her conception from the cosmos began with every spark of inspiration that flooded my body with delight and illuminated my mind to flow with idea after idea of all the people the aerial hammock could serve, the very first time I moved and swayed with the fabric.
            </p>
            <p>
              She was there in the courage I needed to conjure to approach a local pole-fitness studio owner in Fort Collins and ask if I could install aerial hammocks. She met me that day in the welcoming energy I received — synchronistically — from a studio owner who had <em>just</em> become curious about aerial yoga. Aviana was with me on the 14-foot ladder with my hands shaking in fear as I installed each rig, whispering to my inner being that I was on the brink of something big.
            </p>
            <p>
              The name <em>Aviana</em> hadn't made itself fully known yet. She was still in her gestation period. When I was guided back to Illinois, she remained — blessing the journey with a balanced exchange of giving and receiving, of masculine and feminine. I put forth the focused effort to create the first edition of <em>Soaring with the Soul</em>, and launched the first website that began to ripple my intention into the world — which was met, one week later, with an invitation to share my very first aerial yoga teacher training in <strong className="text-aviana-cream font-medium">Bali, Indonesia.</strong>
            </p>
            <p>
              I went to Bali for a month with a group of nine women from different countries. It is where the current Aviana Aerial Yoga Teacher Training curriculum was birthed into being.
            </p>
            <p>
              After Bali, the initial name that had served as the vessel for the program no longer sat right. Aviana was nearing the end of her gestation period. I received another invitation — this time to Ecuador, a week-long tour of the Galápagos at sea co-authoring a book. I arranged for the trip to be a month long with a Young Living farm visit and a work-away at an off-grid farm in the cloud forest. <em>I set the intention before I left that the new name meant to be the vessel for my offerings would be called in.</em>
            </p>
            <p>
              Aviana answered. I was at <strong className="text-aviana-cream font-medium">Sacha Ji in Otavalo, Ecuador</strong> — a sustainable yoga retreat center where the night skies were so vivid you felt you could reach out and touch them. Sitting with the energy of the mountain, reflecting on the divine feminine I'd been surrounded with since arriving in Ecuador — it was as if one of the stars dropped into my body, gave me a burst of clarity, and I said the name <em className="text-aviana-goldSoft not-italic font-medium">Aviana</em> out loud.
            </p>
            <p className="font-serif italic text-[20px] text-aviana-cream">
              The goddess was born.
            </p>
          </div>
          <div className="mt-14">
            <Link to="/aviana" className="btn-gold" data-testid="about-aviana-link">
              Meet the goddess <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* EDUCATION */}
      <section className="py-28 md:py-36 relative overflow-hidden">
        <Mandala size={300} variant="lotus" className="absolute -left-20 bottom-32 text-aviana-plum opacity-[0.07] pointer-events-none hidden md:block" />
        <div className="container-aviana relative">
          <div className="grid lg:grid-cols-12 gap-14 mb-14">
            <div className="lg:col-span-5">
              <div className="relative max-w-md">
                <div className="aspect-[4/5] rounded-[2rem] overflow-hidden ring-2 ring-aviana-gold/40">
                  <img src={img("retreat-portrait")} alt="Holly leading a retreat" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <p className="overline-plum mb-5">Education</p>
              <h2 className="font-display text-5xl md:text-6xl text-aviana-ink leading-[1.05]">
                A <em className="italic font-serif gold-text">student at heart.</em>
              </h2>
              <p className="mt-9 text-aviana-muted leading-[1.95] text-[17px]">
                I have always remained a student — continually deepening my knowledge to better serve those I work with. Below is the foundation, though much of the deepest learning has happened in the field: in arenas, in studios, in retreat centers around the world, and in the quiet hours of integration that follow.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {Object.entries(EDUCATION).map(([category, items]) => (
              <div key={category} className="card-aviana p-8" data-testid={`edu-${category.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <p className="overline mb-5">{category}</p>
                <ul className="space-y-3 text-aviana-inkSoft text-[14px] leading-[1.65]">
                  {items.map((i) => (
                    <li key={i} className="flex gap-3"><span className="text-aviana-gold mt-[7px] text-[8px]">◆</span>{i}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="py-24 md:py-32 bg-aviana-surface">
        <div className="container-aviana grid md:grid-cols-2 gap-8">
          <Link to="/walk-with-me" className="card-aviana p-10 group" data-testid="walk-with-me-link">
            <Mandala size={48} variant="sun" className="text-aviana-gold mb-5 transition-transform group-hover:rotate-90 duration-700" />
            <h3 className="font-display text-3xl text-aviana-ink">Walk with me</h3>
            <p className="mt-3 text-aviana-muted">Every upcoming retreat, training, workshop, and sacred gathering — in one place.</p>
            <p className="mt-6 text-[11px] tracking-[0.22em] uppercase text-aviana-gold">See all offerings →</p>
          </Link>
          <a href="https://avianayoga.substack.com" target="_blank" rel="noopener noreferrer" className="card-aviana p-10 group">
            <Mandala size={48} variant="rose" className="text-aviana-plum mb-5 transition-transform group-hover:rotate-90 duration-700" />
            <h3 className="font-display text-3xl text-aviana-ink">Read with me</h3>
            <p className="mt-3 text-aviana-muted">Holly's Substack — reflections, teachings, and seasonal letters.</p>
            <p className="mt-6 text-[11px] tracking-[0.22em] uppercase text-aviana-gold">Subscribe →</p>
          </a>
        </div>
      </section>
    </div>
  );
}
